<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Information in this section (Introduction) does not need to be learned or memorised and you
        will not be quizzed on it.
      </p>
      <p>
        This information is provided for you to read through and gain an understanding of this
        particular career path before starting your Virtual Job Experience.
      </p>
    </vx-card>
    <vx-card class="mt-base mb-10">
      <h4 class="mb-5">Let’s start your virtual job experience in construction!</h4>

      <div class="vx-row mt-5 flex justify-center">
        <div class="vx-col xl:w-3/4 lg:w-2/3 w-full my-5">
          <p class="mb-5">
            There are many jobs and careers associated with the construction industry. Many more
            than you would think just by looking around a building site.
          </p>
          <h4 class="mb-5">Trades:</h4>
          <p class="mb-5">
            Some of these involve hands-on, practical work. We call these types of jobs trades. Some
            examples of construction trades include bricklayer, carpenter, plumber, and painter.
          </p>
          <h4 class="mb-5">Technicians:</h4>
          <p class="mb-5">
            The work of technicians usually involves supporting professionals such as architects,
            quantity surveyors, and engineers. Some examples of technical positions include buyer,
            roofing technician, Computer-Aided Design operator, and site inspector.
          </p>
          <p class="mb-5">
            There are also many technical roles to fill in the construction sector. Technicians
            bridge the gap between the trades and the professional roles. Although they have
            practical knowledge, they don’t always get involved in the day-to-day hands-on side of
            things.
          </p>
          <h4 class="mb-5">Professionals:</h4>
          <p class="mb-5">
            Other types of jobs involve designing, planning, management, and surveying. We call
            these types of jobs professional. Some examples of construction professions include
            architect, quantity surveyor, project manager, and town planner.
          </p>
          <p class="mb-5">
            Given the huge variety of trades, technical jobs, and professions that make up the
            construction industry, it’ll be no surprise to hear that there is a wide variety of
            entry requirements.
          </p>
          <p class="mb-5">
            In the section below, we’ll take a brief look at some common pathways into each type of
            job. Bear in mind though, to find out the specifics for a job or career of interest,
            you’ll have to do a little more research on your own.
          </p>
          <p class="mb-5">
            If you’re interested in working in construction, but you’re unsure of what capacity,
            this virtual work experience will give you some useful information to help you make an
            informed decision.
          </p>
          <p class="mb-5">
            Even if you already know what you want to do in the construction industry, the material
            and activities in this module will help you gain a broader overview of the industry in
            general.
          </p>
          <p class="mb-5">
            You might even discover a job that appeals that you hadn’t thought of yet!
          </p>
          <p class="mb-5">
            Each of the tabs below will give you information on a different pathway into the
            construction industry.
          </p>
        </div>
        <div class="vx-col xl:w-1/4 lg:w-1/3 w-full my-5">
          <img
            src="@/assets/images/experiences/construction/construction_vertical.jpg"
            alt=""
            class="mx-auto img-responsive"
          />
        </div>
      </div>
    </vx-card>

    <vs-tabs alignment="fixed" v-model="activeTab" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="The Tradesperson">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">Example Trade: Bricklayer</h4>

            <heading size="h3" style-size="h5" class="mb-5 font-normal"><u>Description</u></heading>
            <img
              src="@/assets/images/experiences/construction/tradesperson.jpg"
              alt=""
              class="my-2 ml-2 mb-2 float-right"
            />

            <p class="mb-5">
              Bricklayers lay bricks, blocks, and pre-cut stone in mortar to construct and repair
              walls, partitions, arches, and other structures.
            </p>
            <heading size="h3" style-size="h5" class="mb-5 font-normal"
              ><u>Education Level</u></heading
            >
            <p class="mb-5">
              While it is possible to work as a bricklayer without formal training or qualifications
              by demonstrating technical competency to a potential employer, gaining an AQF
              Certificate III is a much more secure route to a career as a bricklayer.
            </p>
            <p class="mb-5">
              You can find out more about various pathways and training locations using the
              following 2 websites:
            </p>
            <p class="mb-5">
              <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare
              Vocational Education and Training (VET) courses
            </p>
            <p class="mb-10">
              <a href="https://www.aapathways.com.au/career-research/job-pathways" target="_blank"
                >AAPathways</a
              >
              to explore Construction, Plumbing, and Services VET training pathways.
            </p>

            <h4 class="mb-5">Highlights of Becoming a Tradesperson</h4>
            <vs-collapse accordion>
              <vs-collapse-item>
                <div slot="header">1. Job Security</div>
                <p class="mb-5">
                  Trades are some of the most consistently in-demand skills around. Learning a trade
                  will ensure you have access to jobs all over Australia and internationally too.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">2. Get Earning, Quickly!</div>
                <p class="mb-5">
                  Learning a trade doesn’t require you to gain a university qualification, you get
                  qualified quicker and get earning sooner. With the average student debt in
                  Australia at over $21,000, taking up a trade helps you avoid starting your working
                  life in debt too.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">3. Variety</div>
                <p class="mb-5">
                  Working in construction is much more varied than many 9 to 5 options that see you
                  going to the same office to perform the same tasks every day. Your workplace is
                  constantly changing and building projects are varied. You’ll meet and interact
                  with lots of new people too.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">4. Independence</div>
                <p class="mb-5">
                  As a qualified tradesperson and once you gain the necessary experience, you can
                  become self-employed and go out on your own. You can start your own company and
                  hire others to help you. Your earning potential will increase greatly as a result.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">5. Satisfaction</div>
                <p class="mb-5">
                  One of the best aspects of being a tradie is the satisfaction gained from doing
                  something tangible. In so many jobs the results of a day’s work are difficult to
                  see. This isn’t the case in construction. You’ll be able to see the buildings and
                  other structures you helped build for many years to come.
                </p>
              </vs-collapse-item>
            </vs-collapse>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="The Technician">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">
              Example Technicians: Architectural, Building, and Surveying Technicians
            </h4>

            <heading size="h3" style-size="h5" class="mb-5 font-normal"><u>Description</u></heading>
            <img
              src="@/assets/images/experiences/construction/technician.jpg"
              alt=""
              class="my-2 ml-2 mb-2 float-right"
            />

            <p class="mb-5">
              These types of technicians are responsible for performing a wide range of tasks to
              support professionals such as Construction Managers, Surveyors, Engineers, and
              Architects.
            </p>
            <p class="mb-5">
              Some possible tasks they might perform include supervising and inspecting construction
              sites, estimating time, costs, and resources, inspecting plumbing work, and collecting
              and evaluating survey data, and preparing maps and plans.
            </p>
            <heading size="h3" style-size="h5" class="mb-5 font-normal"
              ><u>Education Level</u></heading
            >
            <p class="mb-5">
              Formal qualifications are usually required for work as an Architectural, Building, or
              Surveying Technician. There are a variety of study pathways leading to appropriate
              qualifications in this area, the most common being Vocational Educational and Training
              (VET) and university study.
            </p>
            <p class="mb-5">
              You can find out more about various pathways and potential training locations using
              the following websites:
            </p>
            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> is a
              useful resource to search and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="https://www.compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
            <p class="mb-5">
              <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare
              Vocational Education and Training (VET) courses, providers, and student outcomes.
            </p>
            <p class="mb-10">
              <a href="https://www.aapathways.com.au/career-research/job-pathways" target="_blank"
                >AAPathways</a
              >
              website to explore Construction, Plumbing, and Services VET training pathways.
            </p>

            <h4 class="mb-5">Top Skills for Architectural, Building, and Surveying Technicians</h4>
            <vs-collapse accordion>
              <vs-collapse-item>
                <div slot="header">1. Maths</div>
                <p class="mb-5">
                  If you’re good at maths, then it will serve you well in this type of work. You’ll
                  often need to take measurements, calculate costs, and figure out timelines.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">2. Problem-solving</div>
                <p class="mb-5">
                  Preparation is hugely important in construction, but it isn’t possible to foresee
                  every problem that will arise on a project. The ability to improvise solutions on
                  the spot is an essential skill in this line of work.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">3. Visual-Spatial Reasoning</div>
                <p class="mb-5">
                  A big part of construction relies on the ability to envision things that aren’t
                  there. You’ll need to be able to picture objects in your mind and accurately
                  predict what they’d look like after being moved, rotated, or altered.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">4. An Eye for Detail</div>
                <p class="mb-5">
                  Attention to detail is a very important characteristic to have on a building site.
                  Whether at the planning stage or the quality of workmanship, the ability to check
                  the standard of work is a crucial aspect of a technician’s role.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">5. Management Skills</div>
                <p class="mb-5">
                  Working as a technician will involve some management of workers while interacting
                  on the site, especially if you want to advance your career. You’ll need to ensure
                  you’re able to communicate effectively and have good organisational skills.
                </p>
              </vs-collapse-item>
            </vs-collapse>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="The Professional">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">Example Profession: Construction Manager</h4>
            <heading size="h3" style-size="h5" class="mb-5 font-normal"><u>Description</u></heading>
            <img
              src="@/assets/images/experiences/construction/professional.jpg"
              alt=""
              class="my-2 ml-2 mb-2 float-right"
            />

            <p class="mb-5">
              Construction Managers are responsible for planning, organising, coordinating, and
              supervising building projects. The Construction Manager must make sure the project is
              completed on time and within the allocated budget. They also have a responsibility to
              ensure the project is completed safely.
            </p>
            <heading size="h3" style-size="h5" class="mb-5 font-normal"
              ><u>Education Level</u></heading
            >
            <p class="mb-5">
              Most Construction Managers will have a formal qualification in building or
              construction management or they will have a background in a construction trade. Two of
              the most common pathways are Vocational Educational and Training (VET) and university.
            </p>
            <p class="mb-5">
              You can find out more about various pathways and potential training locations using
              the following websites:
            </p>
            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> is a
              useful resource to search and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="https://www.compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
            <p class="mb-5">
              <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare
              Vocational Education and Training (VET) courses, providers, and student outcomes.
            </p>
            <p class="mb-10">
              <a href="https://www.aapathways.com.au/career-research/job-pathways" target="_blank"
                >AAPathways</a
              >
              website to explore Construction, Plumbing, and Services VET training pathways.
            </p>

            <h4 class="mb-5">Pros and Cons of Being a Construction Manager</h4>
            <h4 class="mb-5">Pros</h4>
            <vs-collapse accordion>
              <vs-collapse-item>
                <div slot="header">1. Get Paid Well</div>
                <p class="mb-5">
                  No doubt about it, this is a well-paying job! And, of course, the more experience
                  and skills you gain over your career, the more you will earn. While the position
                  of construction manager carries a lot of responsibility, you will be well-rewarded
                  for your time and effort.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">2. Call the Shots</div>
                <p class="mb-5">
                  If you are a natural leader and want to make the big decisions in the workplace,
                  this job might just be for you. You will not only have the responsibility for
                  organising and directing large numbers of workers, but you’ll have some financial
                  responsibilities too.
                </p>
              </vs-collapse-item>
            </vs-collapse>
            <h4 class="my-5">Cons</h4>
            <vs-collapse accordion>
              <vs-collapse-item>
                <div slot="header">1. Travel</div>
                <p class="mb-5">
                  One of the downsides of the position is that you may be required to live away from
                  home and family for periods. As a construction manager, you’ll have to travel to
                  where the work is, and this won’t always be a convenient commute away. Sometimes
                  this may mean living away for several months.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">2. Stress</div>
                <p class="mb-5">
                  With all the responsibility of this position, it’ll be no surprise that long hours
                  and stress are a part of the package. With time constraints, personnel issues, and
                  site safety all weighing down on you, you’ll need to learn patience and stress
                  management techniques to help you cope.
                </p>
              </vs-collapse-item>
            </vs-collapse>
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>

    <h4 class="mb-5">Quick Details:</h4>

    <vs-tabs alignment="fixed" v-model="activeTab" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="The Tradesperson">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <p class="mb-5"><strong>Size:</strong> This is a large occupation.</p>
                <p class="mb-5">
                  <strong>Earnings:</strong> Full-time workers on an adult wage earn around $2,070
                  per week (very high compared to the average of $1,460). Earnings tend to be lower
                  when starting out and higher as experience grows.
                </p>
                <p class="mb-5">
                  <strong>Full-time:</strong> Many work full-time (78%, higher than the average of
                  66%).
                </p>
                <p class="mb-5">
                  <strong>Hours:</strong> Full-time workers spend around 43 hours per week at work
                  (compared to an average of 44 hours).
                </p>
                <p class="mb-5">
                  <strong>Age:</strong> The average age is 38 years (compared to the average of 40
                  years).
                </p>
                <p class="mb-5">
                  <strong>Gender:</strong> 1% of workers are female (compared to the average of
                  48%).
                </p>

                <h4 class="mb-5">Tasks:</h4>
                <ul class="default-list">
                  <li>
                    Studying plans and specifications to determine materials required, dimensions,
                    and installation procedures
                  </li>
                  <li>Erecting and dismantling restricted height scaffolding</li>
                  <li>
                    Sealing foundations with damp-resistant materials and spreading layers of mortar
                    to serve as base and binder for blocks using trowels
                  </li>
                  <li>
                    Laying bricks in rows, designs, and shapes, and spreading mortar between joints
                  </li>
                  <li>Embedding blocks in mortar and removing excess mortar</li>
                  <li>Checking vertical and horizontal alignment</li>
                  <li>
                    Cutting, shaping, and polishing stones and bricks using machines and hand tools,
                    and shaping bricks to fit irregular spaces
                  </li>
                  <li>Repairing and maintaining bricks, cement blocks, and related structures</li>
                  <li>Designing and cutting monumental masonry and lettering</li>
                  <li>Constructing walls using stone slabs and large masonry slab blocks</li>
                </ul>
                <p class="mb-5">
                  Source: <a href="https://joboutlook.gov.au" target="_blank">Job Outlook</a>
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/construction/jo1.png"
                  class="mx-auto img-responsive"
                  alt="Bricklayers and Stonemasons job outlook: $2,070 weekly pay, moderate future growth, 32,300 workers, medium skill level, 78% full-time, 43 hours average full-time week, average age 38, 1% female."
                />
              </div>
            </div>
            <vs-alert color="dark" title="Caution" active="true">
              The Australian jobs market is changing in response to the COVID-19 pandemic. These
              estimates do not take account of the impact of COVID-19. They may not reflect the
              current jobs market and should be used and interpreted with extreme caution.
            </vs-alert>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="The Technician">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <p class="mb-5"><strong>Size:</strong> This is a very large occupation.</p>
                <p class="mb-5">
                  <strong>Earnings:</strong> Full-time workers on an adult wage earn around $1,838
                  per week (higher than the average of $1,460). Earnings tend to be lower when
                  starting out and higher as experience grows.
                </p>
                <p class="mb-5">
                  <strong>Full-time:</strong> Most work full-time (89%, much higher than the average
                  of 66%).
                </p>
                <p class="mb-5">
                  <strong>Hours:</strong> Full-time workers spend around 47 hours per week at work
                  (compared to an average of 44 hours).
                </p>
                <p class="mb-5">
                  <strong>Age:</strong> The average age is 42 years (compared to the average of 40
                  years).
                </p>
                <p class="mb-5">
                  <strong>Gender:</strong> 11% of workers are female (compared to the average of
                  48%).
                </p>

                <h4 class="mb-5">Tasks:</h4>
                <ul class="default-list">
                  <li>
                    Assisting Construction Managers, Architects, and Surveyors in planning and
                    organisation
                  </li>
                  <li>Interpreting plans, regulations, and codes of practice</li>
                  <li>Preparing preliminary sketches, working drawings, and specifications</li>
                  <li>Preparing, editing and revising plans, maps, charts and drawings</li>
                  <li>Coordinating works programs</li>
                  <li>
                    Inspecting work and materials for compliance with specifications, regulations,
                    and standards
                  </li>
                  <li>Calculating costs and estimating time scales</li>
                  <li>Collecting data using surveying instruments and photogrammetric equipment</li>
                  <li>Performing routine computations and plotting preliminary data</li>
                </ul>
                <p class="mb-5">
                  Source: <a href="https://joboutlook.gov.au" target="_blank">Job Outlook</a>
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/construction/jo2.png"
                  alt="Architectural, Building and Surveying Technicians job outlook: $1,838 weekly pay, very strong future growth, 77,000 workers, high skill level, 89% full-time, 47 hours average full-time week, average age 42, 11% female."
                  class="mx-auto img-responsive"
                />
              </div>
            </div>
            <vs-alert color="dark" title="Caution" active="true">
              The Australian jobs market is changing in response to the COVID-19 pandemic. These
              estimates do not take account of the impact of COVID-19. They may not reflect the
              current jobs market and should be used and interpreted with extreme caution.
            </vs-alert>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="The Professional">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <p class="mb-5"><strong>Size:</strong> This is a very large occupation.</p>
                <p class="mb-5">
                  <strong>Earnings:</strong> Full-time workers on an adult wage earn around $3,450
                  per week (higher than the average of $1,460). Earnings tend to be lower when
                  starting out and higher as experience grows.
                </p>
                <p class="mb-5">
                  <strong>Full-time:</strong> Most work full-time (89%, much higher than the average
                  of 66%).
                </p>
                <p class="mb-5">
                  <strong>Hours:</strong> Full-time workers spend around 48 hours per week at work
                  (compared to an average of 44 hours).
                </p>
                <p class="mb-5">
                  <strong>Age:</strong> The average age is 44 years (compared to the average of 40
                  years).
                </p>
                <p class="mb-5">
                  <strong>Gender:</strong> 7% of workers are female (compared to the average of
                  48%).
                </p>

                <h4 class="mb-5">Tasks:</h4>
                <ul class="default-list">
                  <li>Interpreting architectural drawings and specifications</li>
                  <li>
                    Coordinating labour resources, and procurement and delivery of materials, plant,
                    and equipment
                  </li>
                  <li>
                    Consulting with Architects, Engineering Professionals and other professionals,
                    and Technical and Trades Workers
                  </li>
                  <li>
                    Negotiating with building owners, property developers, and subcontractors
                    involved in the construction process to ensure projects are completed on time
                    and within budget
                  </li>
                  <li>Preparing tenders and contract bids</li>
                  <li>Operating and implementing coordinated work programs for sites</li>
                  <li>
                    Ensuring adherence to building legislation and standards of performance,
                    quality, cost, and safety
                  </li>
                  <li>Arranging submission of plans to local authorities</li>
                  <li>Building under contract, or subcontracting specialised building services</li>
                  <li>Overseeing the standard and progress of subcontractors' work</li>
                  <li>Arranging building inspections by local authorities.</li>
                </ul>
                <p class="mb-5">
                  Source: <a href="https://joboutlook.gov.au" target="_blank">Job Outlook</a>
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/construction/jo3.png"
                  alt="Construction Managers job outlook: $3,450 weekly pay, strong future growth, 86,600 workers, very high skill level, 89% full-time, 48 hours average full-time week, average age 44, 7% female."
                  class="mx-auto img-responsive"
                />
              </div>
            </div>
            <vs-alert color="dark" title="Caution" active="true">
              The Australian jobs market is changing in response to the COVID-19 pandemic. These
              estimates do not take account of the impact of COVID-19. They may not reflect the
              current jobs market and should be used and interpreted with extreme caution.
            </vs-alert>
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>
  </div>
</template>

<script>
export default {
  data() {
    return {
      activeTab: 0,
    };
  },
};
</script>
